import {
    Box,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { OreveButton } from "components/button/OreveButton.tsx"
import { StyledTextField } from "components/inputs/StyledTextField.tsx"
/*
import { AutochargeVidSection } from "layouts/card-holder/composants/autochargeVidSection.tsx"
*/
import { useForm, Controller } from "react-hook-form"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import React, { useEffect, useState } from "react"
import {
    AutochargeInfo,
    ConfirmedVids,
    DriverInfo,
    VehicleInfo,
} from "types/list-card-holder"
import CheckIcon from "assets/svg/check-icon.svg"
import ErrorIcon from "assets/svg/error-icon.svg"
import {
    OrevePopup,
    OrevePopupContent,
    OrevePopupTitle,
} from "components/popup/OrevePopup.tsx"
import { useCardHolderContext } from "layouts/card-holder/context/useCardHolderContext.ts"

import Truck from "assets/svg/truck.svg?react"
import WarningIcon from "@mui/icons-material/Warning"
import { AutochargeVidSection } from "layouts/card-holder/composants/autochargeVidSection"
import { useFeatureFlags } from "context/FeatureFlagsContext"
interface IUpdateDriverFormProps {
    firstName: string
    lastName: string
}

interface IUpdateVehicleFormProps {
    firstName: string
    lastName: string
    email: string
    autocharge: {
        status: boolean
        idTagUid: string
    }[]
}

type AutochargeCreationResponse = {
    data: unknown | null
    ok: boolean
    status: number
    error: object | null
}

export const UpdateCardHolderPopup = ({
    onVidCreationError,
}: {
    onVidCreationError?: (vids: string[]) => void
}) => {
    const {
        openUpdateCardHolder: open,
        onClosePopup: handleClose,
        driverSelected: driver,
        setUpdatedCardHolderId,
    } = useCardHolderContext()

    const { cardHolderService } = useMmsService()
    const { useUpdateCardHolder } = cardHolderService

    const {
        success,
        isLoading,
        status,
        sendRequest: updateDriver,
    } = useUpdateCardHolder(driver?.id ?? "")

    // Un 409 (Conflict) sur le PUT /v2/card-holders/{id} est considéré comme une
    // réussite partielle : la modification a bien été prise en compte. Pour
    // l'instant on l'affiche comme un succès normal.
    const isSuccess = (success ?? false) || status === 409

    const [displayResponse, setDisplayResponse] = useState(false)

    // When closing after a successful update, store the card-holder id so its
    // row shows a loader during the GET /v2/card-holders refetch triggered by
    // onClosePopup.
    const handleClosePopup = () => {
        if (displayResponse && isSuccess && driver) {
            setUpdatedCardHolderId(driver.id)
        }
        handleClose()
    }

    const onUpdateDriverOrVehicle = async (
        updatedDriver: IUpdateDriverFormProps
    ) => {
        await updateDriver(updatedDriver)
        setDisplayResponse(true)
    }

    useEffect(() => {
        if (open) setDisplayResponse(false)
    }, [open])

    if (!driver) return null

    return (
        <OrevePopup open={open} handleClose={() => handleClosePopup()}>
            {!displayResponse ? (
                <PopupMain
                    vehicleOrDriver={driver as DriverInfo}
                    isLoading={isLoading}
                    onUpdateDriverOrVehicle={onUpdateDriverOrVehicle}
                    onVidCreationError={onVidCreationError}
                />
            ) : (
                <PopupFeedback
                    success={isSuccess}
                    isLoading={isLoading}
                    handleClose={() => handleClosePopup()}
                    isConflict={status === 409}
                />
            )}
        </OrevePopup>
    )
}

const PopupMain = ({
    vehicleOrDriver,
    isLoading,
    onUpdateDriverOrVehicle,
    onVidCreationError,
}: {
    vehicleOrDriver: DriverInfo | VehicleInfo
    isLoading: boolean
    onUpdateDriverOrVehicle: (data: IUpdateDriverFormProps) => void
    onVidCreationError?: (vids: string[]) => void
}) => {
    const { isVehicle } = useCardHolderContext()

    return (
        <React.Fragment>
            <OrevePopupTitle>
                <UpdatePopupTitle
                    isVehicle={isVehicle}
                    data-cy={
                        isVehicle
                            ? "vehicle__update--title"
                            : "driver__update--title"
                    }
                />
            </OrevePopupTitle>
            <OrevePopupContent>
                {isVehicle ? (
                    <UpdateVehicleForm
                        onUpdateVehicle={onUpdateDriverOrVehicle}
                        isLoading={isLoading}
                        vehicle={vehicleOrDriver as VehicleInfo}
                        onVidCreationError={onVidCreationError}
                    />
                ) : (
                    <UpdateDriverForm
                        onUpdateDriver={onUpdateDriverOrVehicle}
                        isLoading={isLoading}
                        driver={vehicleOrDriver as DriverInfo}
                    />
                )}
            </OrevePopupContent>
        </React.Fragment>
    )
}

const UpdateDriverForm = ({
    onUpdateDriver,
    isLoading,
    driver,
}: {
    onUpdateDriver: (data: IUpdateDriverFormProps) => void
    isLoading: boolean
    driver: DriverInfo
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<{ firstName: string; lastName: string; email: string }>({
        defaultValues: {
            firstName: driver.firstName ?? "",
            lastName: driver.lastName ?? "",
            email: driver.email ?? "",
        },
        reValidateMode: "onSubmit",
    })
    const theme = useTheme()

    const onSubmitUpdateForm = (data: IUpdateDriverFormProps) => {
        const formattedDriver = {
            lastName: data.lastName.trim(),
            firstName: data.firstName.trim(),
        }
        onUpdateDriver(formattedDriver)
    }
    const [isFirstNameChanged, setIsFirstNameChanged] = useState<boolean>(true)
    const [isLastNameChanged, setIsLastNameChanged] = useState<boolean>(true)

    return (
        <form onSubmit={handleSubmit(onSubmitUpdateForm)}>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{
                            required: "Ce champ est requis",
                            validate: (value) =>
                                value.trim() !== "" || "Ce champ est requis",
                            onChange: (e) => {
                                if (e.target.value.trim() == driver.lastName) {
                                    setIsLastNameChanged(true)
                                } else {
                                    setIsLastNameChanged(false)
                                }
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-update-lastName"
                                label="Nom"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.lastName}
                                helperText={errors.lastName?.message || ""}
                                {...field}
                                data-cy={"driver__update--lastName"}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: "Ce champ est requis",
                            validate: (value) =>
                                value.trim() !== "" || "Ce champ est requis",
                            onChange: (e) => {
                                if (
                                    e.target.value.trim() === driver.firstName
                                ) {
                                    setIsFirstNameChanged(true)
                                } else {
                                    setIsFirstNameChanged(false)
                                }
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-update-firstName"
                                label="Prénom"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.firstName}
                                helperText={
                                    errors.firstName
                                        ? "Ce champ est requis"
                                        : ""
                                }
                                {...field}
                                data-cy={"driver__update--firstName"}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Controller
                        name="email"
                        control={control}
                        disabled={true}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-update-email"
                                label="Email"
                                disabled={true}
                                bgcolor={theme.palette.grey[200]}
                                {...field}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <OreveButton
                type="submit"
                value="Modifier un conducteur"
                variant="contained"
                color="primary"
                disabled={isFirstNameChanged && isLastNameChanged}
                loading={isLoading}
                sx={{ mt: "2.25rem" }}
                data-cy={"driver__update--submit"}
            />
        </form>
    )
}

interface UpdatePopupTitleProps {
    isVehicle: boolean
    "data-cy"?: string
}

const UpdatePopupTitle = ({ isVehicle }: UpdatePopupTitleProps) => {
    const title = isVehicle ? "Modifier un véhicule" : "Modifier un conducteur"
    const theme = useTheme()
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.5rem",
                justifyContent: "center",
            }}
        >
            {isVehicle && (
                <Truck
                    style={{
                        color: theme.palette.success.main,
                        marginRight: 20,
                    }}
                />
            )}
            <Typography variant="headlineSmall" component="div">
                {title}
            </Typography>
        </Box>
    )
}

const UpdateVehicleForm = ({
    onUpdateVehicle,
    isLoading,
    vehicle,
    onVidCreationError,
}: {
    onUpdateVehicle: (data: IUpdateDriverFormProps) => void
    isLoading: boolean
    vehicle: VehicleInfo
    onVidCreationError?: (vids: string[]) => void
}) => {
    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
        getValues,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: vehicle.description ? vehicle.description : "",
            lastName: vehicle.licensePlate ? vehicle.licensePlate : "",
            email: vehicle.email ? vehicle.email : "",
            autocharge: Array.isArray(vehicle.autocharge)
                ? vehicle.autocharge
                : [],
        },
        reValidateMode: "onSubmit",
    })
    const theme = useTheme()
    // true means "unchanged" (button disabled). We keep existing prop usage: disabled={isVehicleChanged}
    const [isVehicleChanged, setIsVehicleChanged] = useState<boolean>(true)

    const { vidAutochargeEnabled } = useFeatureFlags()
    const [confirmedVids, setConfirmedVids] = useState<ConfirmedVids>([])

    // Compute which autocharge indices are actually displayed (same condition as in render)
    const displayedAutochargeIndices = React.useMemo(() => {
        const indices: number[] = []
        for (let i = 0; i < 4; i++) {
            const vid = (vehicle.autocharge?.[i]?.idTagUid ?? "").trim()
            if (vid || vehicle.autocharge?.[i]) indices.push(i)
        }
        return indices
    }, [vehicle])

    const recomputeUnchanged = React.useCallback(
        (opts?: {
            nextDescription?: string
            switchIndex?: number
            nextSwitchValue?: boolean
        }) => {
            const current = getValues()
            const currentDesc = (
                opts?.nextDescription ??
                current.firstName ??
                ""
            ).trim()
            const initialDesc = (vehicle.description ?? "").trim()
            if (currentDesc !== initialDesc) return setIsVehicleChanged(false)

            for (const i of displayedAutochargeIndices) {
                const initialEnabled = vehicle.autocharge?.[i]?.status ?? true
                const currentEnabled =
                    opts &&
                    opts.switchIndex === i &&
                    typeof opts.nextSwitchValue === "boolean"
                        ? opts.nextSwitchValue
                        : !!current.autocharge?.[i]?.status
                if (currentEnabled !== initialEnabled) {
                    return setIsVehicleChanged(false)
                }
            }

            const initialVids = (vehicle.autocharge ?? [])
                .filter((a) => a.idTagUid.trim() !== "")
                .slice(0, 4)
                .map((a) => ({
                    vid: a.idTagUid.trim(),
                    enabled: a.status ?? true,
                }))

            if (initialVids.length !== confirmedVids.length) {
                return setIsVehicleChanged(false)
            }

            const sameVids = initialVids.every((initialVid, index) => {
                const currentVid = confirmedVids[index]

                return (
                    currentVid?.vid === initialVid.vid &&
                    (currentVid?.enabled ?? true) ===
                        (initialVid.enabled ?? true)
                )
            })

            if (!sameVids) {
                return setIsVehicleChanged(false)
            }
            // No diffs found => unchanged
            setIsVehicleChanged(true)
        },
        [displayedAutochargeIndices, getValues, vehicle, confirmedVids]
    )

    const initialConfirmedVids: ConfirmedVids =
        vehicle.autocharge
            ?.filter((a) => a.idTagUid.trim() !== "")
            .slice(0, 4)
            .map((a) => ({
                vid: a.idTagUid.trim(),
                enabled: a.status,
            })) ?? []

    useEffect(() => {
        recomputeUnchanged()
    }, [confirmedVids, recomputeUnchanged])

    useEffect(() => {
        const next: ConfirmedVids = Array.isArray(vehicle.autocharge)
            ? vehicle.autocharge
                  .filter((a) => a.idTagUid.trim() !== "")
                  .slice(0, 4)
                  .map((a) => ({
                      vid: a.idTagUid.trim(),
                      enabled: a.status ?? true,
                  }))
            : []

        setConfirmedVids(next)
    }, [vehicle])

    interface AutochargeStatus {
        idTagUid: string | null
        userId: number | string | null
        autoChargeEnabled: boolean | null
    }
    const { cardHolderService } = useMmsService()
    const { usePostAutochargeToVehicle } = cardHolderService
    const { sendRequest: addAutochargeToVehicle } = usePostAutochargeToVehicle()

    const onSubmitUpdateForm = async (data: IUpdateVehicleFormProps) => {
        const existingByVid = new Map<string, AutochargeInfo>()
        ;(vehicle.autocharge ?? []).forEach((a) => {
            if (typeof a.idTagUid === "string" && a.idTagUid.trim() !== "") {
                existingByVid.set(a.idTagUid.trim(), a)
            }
        })

        const entries = confirmedVids
            .filter(({ vid }) => vid.trim() !== "")
            .slice(0, 4)

        const existingAutochargeToUpdate: AutochargeInfo[] = []
        const newAutochargeToCreate: AutochargeStatus[] = []

        for (const { vid: vidRaw, enabled } of entries) {
            const vid = vidRaw.trim()
            const existing = existingByVid.get(vid)

            if (existing) {
                existingAutochargeToUpdate.push({
                    id: existing.id ?? null,
                    idTagUid: vid,
                    status: typeof enabled === "boolean" ? enabled : true,
                })
            } else {
                newAutochargeToCreate.push({
                    idTagUid: vid,
                    autoChargeEnabled:
                        typeof enabled === "boolean" ? enabled : true,
                    userId: vehicle.id || null,
                })
            }
        }

        const formattedVehicle = {
            lastName: data.lastName.trim(),
            firstName: data.firstName.trim(),
            email: vehicle.licensePlate + "@oreve-vehicle.com",
            autocharge: existingAutochargeToUpdate,
        }

        await onUpdateVehicle(formattedVehicle)

        const failedVids: string[] = []

        for (const vid of newAutochargeToCreate) {
            const result = (await addAutochargeToVehicle(
                vid
            )) as AutochargeCreationResponse

            // The 409 (Conflict) isn't a conflict, it trigger when
            // you try to create a vid but you doesn't allow autocharge on the vehicle
            if (!result.ok && result.status !== 409 && vid.idTagUid) {
                failedVids.push(vid.idTagUid)
            }
        }

        if (failedVids.length > 0) {
            onVidCreationError?.(failedVids)
        } else {
            onVidCreationError?.([])
        }
    }

    const parseImmatCountry = () => {
        const immat = vehicle.email.split("+")
        if (immat[1] && immat[1].toLowerCase().startsWith("fr")) {
            return "France"
        }
        return "Autre"
    }

    const gridSize = { xs: 12, sm: 6 }

    return (
        <form
            onSubmit={handleSubmit(onSubmitUpdateForm)}
            style={{ paddingBottom: "10px" }}
        >
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            maxLength: 30,
                            required: true,
                            onChange: (e) => {
                                const nextVal = e.target.value as string
                                recomputeUnchanged({ nextDescription: nextVal })
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-description"
                                label="Nom du véhicule"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.firstName}
                                helperText={
                                    errors.firstName
                                        ? errors.firstName.type === "maxLength"
                                            ? "Le nom du véhicule doit faire au maximum 30 caractères"
                                            : ""
                                        : ""
                                }
                                {...field}
                                data-cy={
                                    "form_vehicle__add-vehicle--description"
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid size={gridSize}>
                    <InputLabel id="type-matricule-select-label" sx={visuallyHidden}>
                        Pays
                    </InputLabel>
                    <Select
                        fullWidth
                        sx={{
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            ".MuiOutlinedInput-notchedOutline": {
                                border: "none",
                            },
                            // Focus outline
                        }}
                        disabled={true}
                        labelId="type-matricule-select-label"
                        id="type-matricule"
                        data-cy={
                            "form_vehicle__add-vehicle--license-plate-type"
                        }
                        value={parseImmatCountry()}
                    >
                        <MenuItem
                            value="France"
                            data-cy={
                                "form_vehicle__add-vehicle--license-plate-type-france"
                            }
                        >
                            France
                        </MenuItem>
                        <MenuItem
                            value="Autre"
                            data-cy={
                                "form_vehicle__add-vehicle--license-plate-type-other"
                            }
                        >
                            Autre
                        </MenuItem>
                    </Select>
                </Grid>
                <Grid size={gridSize}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-licensePlate"
                                label="Plaque d'immatriculation"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.email}
                                {...field}
                                value={vehicle.licensePlate}
                                disabled={true}
                                data-cy={
                                    "form_vehicle__add-vehicle--license-plate"
                                }
                            />
                        )}
                    />
                </Grid>

                {vidAutochargeEnabled && (
                    <Box sx={{ width: "100%", mt: 2 }}>
                        <AutochargeVidSection
                            control={control}
                            errors={errors}
                            setError={setError}
                            clearErrors={clearErrors}
                            getValues={getValues}
                            setValue={setValue}
                            confirmedVids={confirmedVids}
                            setConfirmedVids={setConfirmedVids}
                            max={4}
                            index={0}
                            cancelDelete={false}
                            isUpdateMode={true}
                            isVehicleInactive={!vehicle.active}
                            initialConfirmedVids={initialConfirmedVids}
                        />
                    </Box>
                )}
            </Grid>
            <OreveButton
                type="submit"
                value="Modifier un véhicule"
                variant="contained"
                color="primary"
                disabled={isVehicleChanged}
                loading={isLoading}
                sx={{
                    mt: "2.25rem",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                }}
                data-cy={"vehicle__update--submit"}
            />
        </form>
    )
}

const PopupFeedback = ({
    success,
    isLoading,
    handleClose,
    isConflict = false,
}: {
    success: boolean
    isLoading: boolean
    handleClose: () => void
    isConflict?: boolean
}) => {
    const { isVehicle: isVehicle } = useCardHolderContext()
    const text = isVehicle ? "véhicule" : "conducteur"

    return (
        <>
            <OrevePopupTitle>
                {success ? (
                    <>
                        <img src={CheckIcon} alt={"check-icon"} />
                        <Typography variant="headlineSmall" component="div">
                            Le {text} a été modifié avec succès
                        </Typography>
                    </>
                ) : (
                    <>
                        <img src={ErrorIcon} alt={"error-icon"} />
                        <Typography
                            variant="headlineSmall"
                            component="div"
                            color="error"
                        >
                            Une erreur est survenue lors de la modification du{" "}
                            {text}
                        </Typography>
                    </>
                )}
            </OrevePopupTitle>
            <OrevePopupContent>
                <OreveButton
                    value={`Retourner à la liste des ${text}s`}
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    onClick={() => handleClose()}
                    data-cy={
                        isVehicle
                            ? "vehicle__update--back-to-list"
                            : "driver__update--back-to-list"
                    }
                />
                {isConflict && (
                    <Typography
                        variant="body"
                        component="div"
                        align="center"
                        sx={{
                            mt: "1rem",
                            bgcolor: "status.warning",
                            color: "common.black",
                            borderRadius: 10,
                            p: "0.75rem 1rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                        }}
                        data-cy={"vehicle__update--autocharge-warning"}
                    >
                        <WarningIcon
                            fontSize="small"
                            sx={{ color: "warning.main" }}
                        />
                        Votre organisation n'a pas activé l'autocharge, veuillez
                        contacter votre manager pour l'activer !
                    </Typography>
                )}
            </OrevePopupContent>
        </>
    )
}
